<script lang="ts" setup>
import type { PropType } from "@vue/runtime-core";
import { computed, defineProps, onMounted, ref } from "vue";
import { useStore } from "vuex";

const props = defineProps({
  title: {
    type: String,
  },
  color: {
    type: String as PropType<
      | "primary"
      | "white"
      | "transparent-white"
      | "transparent-black"
      | "transparent-primary"
      | "grey"
    >,
    default: "primary",
  },
  size: {
    type: String as PropType<
      "extra-large" | "large" | "medium" | "small" | "circle"
    >,
    default: "medium",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();

const classes = computed(() => {
  return [
    props.color,
    props.disabled || props.loading ? "disabled" : "",
    props.size,
    props.size === "large" || props.size === "extra-large"
      ? "button-large"
      : "button-medium",
  ];
});
</script>

<template>
  <button class="base-button color-basic" :class="classes" :disabled="disabled">
    <slot name="default" v-if="$slots.default"></slot>
    <span v-else>{{ title }}</span>
  </button>
</template>

<style lang="scss" scoped>
@import "@/assets/style/main";

.base-button {
  border-radius: 30px;
  padding: 18px 40px;
  border: 1px solid transparent;
  transition: background 0.4s;
  cursor: pointer;
  width: 100%;
  @media (max-width: 768px) {
    padding: 13px 15px;
    font-size: 11px;
  }

  &.primary {
    background: var(--color-primary);
    color: var(--color-white);

    &:hover {
      background: color-mix(in srgb, var(--color-primary), #000 20%);
    }

    &:active {
      background: color-mix(in srgb, var(--color-primary), #000 20%);
      border: 1px solid var(--color-white);
    }
  }

  &.grey {
    background: var(--color-bg-primary);

    &:hover {
      background: var(--color-white);
    }

    &:active {
      background: var(--color-bg-primary);
      border: 1px solid var(--color-text-secondary);
    }
  }

  &.white {
    background: var(--color-white);
    color: var(--color-text-primary);

    &:hover {
      background: var(--color-bg-primary);
    }

    &:active {
      background: var(--color-white);
      border: 1px solid var(--color-text-secondary);
    }
  }
  &.extra-large {
    padding: 41px 40px;
    border-radius: 100px;
    @media (max-width: 768px) {
      padding: 24px 20px;
      border-radius: 48px;
      font-size: 12px;
    }
  }

  &.large {
    padding: 19px 40px;
    border-radius: 30px;
    @media (max-width: 768px) {
      //padding: 17px 40px;
      //border-radius: 48px;
    }
  }

  &.medium {
    border-radius: 30px;
    padding: 16px 40px;
    @media (max-width: 768px) {
      padding: 14px 20px;
    }
  }

  &.small {
    border-radius: 30px;
    padding: 16px 30px;
    @media (max-width: 768px) {
      padding: 16px 22px;
    }
  }

  &.circle {
    border-radius: 30px;
    padding: 11px;
  }

  &:active {
    background: var(--color-white);
    border: 1px solid var(--color-secondary);
  }

  &.transparent-white {
    background: transparent;
    color: var(--color-white);

    &:hover {
      background: color-mix(in srgb, #{var(--color-white)} 15%, transparent);
    }

    &:active {
      background: var(--color-white);
      border: 1px solid var(--color-secondary);
    }
  }

  &.transparent-black {
    background: transparent;
    color: var(--color-text-primary);

    &:hover {
      background: color-mix(in srgb, #{var(--color-white)} 15%, transparent);
    }

    &:active {
      background: var(--color-white);
      border: 1px solid var(--color-secondary);
    }
  }

  &.transparent-primary {
    background: transparent;
    color: var(--color-primary);

    &:active {
      background: var(--color-white);
      border: 1px solid var(--color-secondary);
    }
  }

  &.disabled {
    background: var(--color-grey-blue);
    pointer-events: none;
    touch-action: none;

    &.disabled {
      background: var(--color-grey-blue);
    }
    &:hover {
      background: var(--color-grey-blue);
    }

    &:active {
      background: var(--color-grey-blue);
    }
  }
}
</style>
